import { useCallback, useEffect, useRef, useState } from 'react';

import {
  HelperPong,
  isHelperReady,
  isOrphanSignInResult,
  pingSignInHelper,
} from '../lib/signInHelper';

// FD-592 (FD-ADR-047 §5) — is the AirBridge sign-in helper extension here?
//
// Detection is a PING over the relay with a short timeout. The relay's own
// `airbridge.ready` post usually fires at document_start, before this page is
// listening, so it is a bonus signal — never the primary one. It fires again when
// the operator approves this console in the extension popup, and (FD-607) when the
// extension injects a fresh relay into this already-open tab after an install,
// update, re-enable or reload. Re-pinging on it turns the check green without a
// focus change.
//
// RE-CHECKED ON WINDOW FOCUS: the store page opens in a new tab, the operator
// installs, and comes back. The check must notice without a reload, because a
// reload is exactly what an in-flight browser sign-in cannot survive.
//
// ADVISORY. This decides copy and nothing else. The browser sign-in works without
// the helper (one paste), and the Activate button never reads this.

export type HelperStatus = 'checking' | 'present' | 'absent';

export interface SignInHelperState {
  status: HelperStatus;
  pong: HelperPong | null;
  /**
   * A helper RESULT arrived for a sign-in this page is not waiting on. After a
   * reload that is exactly what a finishing sign-in looks like: the extension still
   * delivers to the tab, but the PKCE verifier died with the previous page. Used
   * only to say so — it is never redeemed.
   */
  orphanResult: boolean;
  /**
   * FD-607: a ping succeeded at least once while this page was open. Latched, so a
   * later check that times out (a relay orphaned by an extension reload, say) does
   * not erase the fact that the helper WAS here. Used to tell "the helper did not
   * respond in this tab" (reload it) apart from "there is no helper" (paste).
   */
  everDetected: boolean;
  dismissOrphan: () => void;
  recheck: () => void;
}

export function useSignInHelper(enabled = true): SignInHelperState {
  const [status, setStatus] = useState<HelperStatus>('checking');
  const [pong, setPong] = useState<HelperPong | null>(null);
  const [orphanResult, setOrphanResult] = useState(false);
  const [everDetected, setEverDetected] = useState(false);
  const generation = useRef(0);

  const recheck = useCallback(() => {
    const mine = ++generation.current;
    void pingSignInHelper().then((answer) => {
      // A later check supersedes an earlier one that answered late.
      if (mine !== generation.current) return;
      setPong(answer);
      setStatus(answer ? 'present' : 'absent');
      if (answer) setEverDetected(true);
    });
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    recheck();

    const onFocus = () => recheck();
    const onMessage = (event: MessageEvent) => {
      if (isHelperReady(event)) {
        recheck();
        return;
      }
      if (isOrphanSignInResult(event)) setOrphanResult(true);
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('message', onMessage);
    return () => {
      generation.current++;
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('message', onMessage);
    };
  }, [enabled, recheck]);

  const dismissOrphan = useCallback(() => setOrphanResult(false), []);

  return { status, pong, orphanResult, everDetected, dismissOrphan, recheck };
}
